import React, { useEffect, useState } from 'react'
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TablePagination,
  Box,
  Button
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import TrackingBody from './TableBody'
import {
  clearPrepaymentsMessage,
  getPrepayments,
  prepaymentsLoader,
  requestInvoice,
  selectPrepaymentAmount,
  selectPrepayments,
  selectTotalPrepayment
} from './state/prepaymentsSlice'
import { userProfile } from '@views/profile/state/profileSlice'
import UploadInvoiceModal from './UploadInvoiceModal'
import { formatCurrency } from '../../utils/format'
import { createPrepaymentPdf } from '../../utils/buildInvoice'
import { Invoice, postInvoice } from '@views/commissionsReferrals/state/commissionsSlice'
import { reserveInvoiceDraftService } from '@http/commissionsService'
import { selectCredentials } from '@views/loginView/state/loginSlice'
import { hasCompletePaymentProfile } from '../../utils/paymentProfile'

export type Order = 'asc' | 'desc'

type ReservedInvoice = {
  _id: string
  serial: string
  serialNumber: number
  status: 'draft' | 'created' | 'paid'
}

// const lastBusinessDayOfMonth = [
//   '2026-01-30',
//   '2026-02-27',
//   '2026-02-28',
//   '2026-03-02',
//   '2026-03-31',
//   '2026-04-01',
//   '2026-04-02',
//   '2026-04-30',
//   '2026-05-29',
//   '2026-06-30',
//   '2026-07-31',
//   '2026-08-31',
//   '2026-09-30',
//   '2026-10-30',
//   '2026-11-30',
//   '2026-12-31'
// ]

// const offset = -5
// const date = new Date(new Date().getTime() + offset * 3600 * 1000).toISOString().split('T')[0]

const PrepaymentsView: React.FC = () => {
  const [page, setPage] = useState(0)
  const [isModalOpen, setModalOpen] = useState(false)
  const [isSubmittingInvoice, setIsSubmittingInvoice] = useState(false)
  const [reservedInvoice, setReservedInvoice] = useState<ReservedInvoice | null>(null)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  // const [serial, setSerial] = useState('')
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const rows: any[] = useSelector(selectPrepayments)
  const { name, document, phone, accountData } = useSelector(userProfile)
  const { cocap } = useSelector(selectCredentials)
  const prepaymentAmount = useSelector(selectPrepaymentAmount)
  const totalPrepayment = useSelector(selectTotalPrepayment)
  const { isLoading, isFailed, isSuccess, message } = useSelector(prepaymentsLoader)
  // For now on all days are enabled.
  const isLastDay = true
  const hasPaymentProfile = hasCompletePaymentProfile(accountData)

  useEffect(() => {
    dispatch(getPrepayments())
    setPage(0)
    setRowsPerPage(10)
  }, [dispatch])

  useEffect(() => {
    setReservedInvoice(null)
  }, [rows, totalPrepayment, prepaymentAmount])

  useEffect(() => {
    if (isSubmittingInvoice && isSuccess) {
      setIsSubmittingInvoice(false)
      setModalOpen(false)
    }
  }, [isSubmittingInvoice, isSuccess])

  useEffect(() => {
    if (isFailed && message) {
      enqueueSnackbar(message, { variant: 'error' })
      dispatch(clearPrepaymentsMessage())
      setIsSubmittingInvoice(false)
    }
  }, [dispatch, enqueueSnackbar, isFailed, message])

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const buildInvoicePayload = (file?: string): Invoice => ({
    commissions: rows.map(item => ({
      personId: item._id,
      name: item.name,
      amount: prepaymentAmount
    })),
    amount: totalPrepayment,
    ...(file && { file }),
    type: 'prepayment'
  })

  const downloadPdf = async () => {
    try {
      const draft = await reserveInvoiceDraftService(cocap || '', buildInvoicePayload())
      createPrepaymentPdf(
        `CC-anticipo-${new Date().toISOString().split('T')[0]}`,
        draft.serial,
        name,
        document,
        Math.round(totalPrepayment),
        formatCurrency(totalPrepayment),
        rows.map(item => [
          item.name,
          formatCurrency(Math.round(item.stages.fee / 1.19)),
          formatCurrency(Math.round(prepaymentAmount))
        ]),
        accountData?.bankName,
        accountData?.accountType,
        accountData?.accountNumber,
        accountData?.physicalAddress,
        phone
      )
      setReservedInvoice(draft)
    } catch (error) {
      const message =
        (error as any)?.response?.data?.message || 'No fue posible reservar la cuenta de cobro para descargar el PDF.'
      enqueueSnackbar(message, { variant: 'error' })
    }
  }

  const onLoadInvoice = async (file: string) => {
    if (isSubmittingInvoice || isLoading) return
    if (!reservedInvoice?._id) {
      enqueueSnackbar('Debes descargar la cuenta de cobro antes de subirla.', { variant: 'error' })
      return
    }

    setIsSubmittingInvoice(true)
    dispatch(requestInvoice())
    dispatch(
      postInvoice({
        ...buildInvoicePayload(file),
        invoiceId: reservedInvoice._id
      })
    )
  }

  const disabledInvoice = !hasPaymentProfile || rows.length <= 0
  return (
    <>
      <div className="center_view padding_view" id="commissions-view">
        <TableContainer className="container-table" component={Paper}>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell className="table_header" style={{ color: 'white' }} align="left">
                  REFERIDO
                </TableCell>
                <TableCell className="table_header" style={{ color: 'white' }} align="center">
                  OFICINA
                </TableCell>
                <TableCell className="table_header" style={{ color: 'white' }} align="center">
                  EXITOSO
                </TableCell>
                <TableCell className="table_header" style={{ color: 'white' }} align="center">
                  HONORARIOS
                </TableCell>
                <TableCell className="table_header" style={{ color: 'white' }} align="center">
                  HONORARIOS SIN IVA
                </TableCell>
                <TableCell className="table_header" style={{ color: 'white' }} align="center">
                  ANTICIPO
                </TableCell>
              </TableRow>
            </TableHead>
            <TrackingBody users={rows || []} rowsPerPage={rowsPerPage} page={page} isLoading={false} isTeam={true} />
          </Table>
          <div className="pagination-row">
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
            <div>Total anticipo: {formatCurrency(totalPrepayment)}</div>
          </div>
        </TableContainer>
        <br />
        <Box width="100%" justifyContent="right" display="flex" marginBottom="1rem">
          <span>
            {hasPaymentProfile
              ? 'Tu perfil de pago está completo para solicitar anticipos.'
              : 'Completa tu perfil con datos bancarios, dirección física, link de cédula y link de RUT en '}
            {!hasPaymentProfile && <a href="/profile">Datos bancarios para pago de anticipos</a>}
          </span>
        </Box>
        {/* {totalPrepayment <= 0 ? (
          <Box>
            Recuerda que debiste referir 3 o mas personas el mes anterior para
            que se te pague de manera anticipada
          </Box>
        ) : (
          ''
        )} */}
        {isLastDay ? (
          <Box display="flex" justifyContent="right" width="100%">
            <Button
              variant="contained"
              color="primary"
              onClick={() => setModalOpen(true)}
              disabled={disabledInvoice || !reservedInvoice || totalPrepayment <= 0 || isSubmittingInvoice}
              style={{ marginRight: '10px' }}
            >
              Subir cuenta de cobro
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={downloadPdf}
              disabled={disabledInvoice || totalPrepayment <= 0}
            >
              {reservedInvoice ? 'Volver a descargar' : 'Descargar cuenta de cobro'}
            </Button>
          </Box>
        ) : (
          <Box display="flex" justifyContent="right" width="100%">
            Recuerda que podrás solicitar tu anticipo el ultimo dia hábil del mes
          </Box>
        )}
        <UploadInvoiceModal
          open={isModalOpen}
          onAccept={onLoadInvoice}
          onReject={() => !isSubmittingInvoice && setModalOpen(false)}
          isSubmitting={isSubmittingInvoice}
        />
      </div>
    </>
  )
}

export default PrepaymentsView
